import React, { useState, useEffect, useRef } from 'react';
import './Products.css';

function keyFromFilename(f) { return f.replace(/\.[^.]+$/, ''); }
function labelFromKey(k)    { return k.replace(/_/g, ' '); }

function matchesSearch(key, data, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  const label = labelFromKey(key).toLowerCase();
  const keywords = (data.keywords || []).join(' ').toLowerCase();
  const desc = (data.desc || '').toLowerCase();
  return label.includes(q) || keywords.includes(q) || desc.includes(q);
}

export default function Products() {
  const ref = useRef(null);
  const [productData, setProductData] = useState({});
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [showAll, setShowAll] = useState(false);
  const INITIAL_COUNT = 6;

  useEffect(() => {
    fetch('/products/price_description.json')
      .then((r) => r.json())
      .then((data) => setProductData(data))
      .catch(() => setProductData({}));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || Object.keys(productData).length === 0) return;

    // Small delay so new cards are in the DOM before we observe
    const timer = setTimeout(() => {
      const targets = el.querySelectorAll('.reveal:not(.visible)');
      if (!targets.length) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry, i) => {
            if (entry.isIntersecting) {
              setTimeout(() => entry.target.classList.add('visible'), i * 80);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.05 }
      );
      targets.forEach((t) => observer.observe(t));
    }, 50);

    return () => clearTimeout(timer);
  }, [productData, query, activeCategory, showAll]);

  // Build category list dynamically from JSON
  const categories = ['All', ...Array.from(
    new Set(Object.values(productData).map((d) => d.category).filter(Boolean))
  ).sort()];

  const filtered = Object.keys(productData).filter((key) => {
    const data = productData[key];
    const categoryMatch = activeCategory === 'All' || data.category === activeCategory;
    return categoryMatch && matchesSearch(key, data, query);
  });

  // When searching or filtering, show all results; otherwise respect showAll
  const isFiltering = query || activeCategory !== 'All';
  const visible = isFiltering ? filtered : (showAll ? filtered : filtered.slice(0, INITIAL_COUNT));

  return (
    <section className="products-section" id="products" ref={ref}>
      <div className="section-header reveal">
        <h2>Our Products</h2>
        <p>Straight from the farm — no chemicals, no shortcuts.</p>
      </div>

      {/* Search + Filter row */}
      <div className="products-controls reveal">
        <div className="search-wrap">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="product-search"
            placeholder="Search by name, season, type…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search products"
          />
          {query && (
            <button className="search-clear" onClick={() => setQuery('')} aria-label="Clear search">✕</button>
          )}
        </div>

        <div className="category-wrap">
          <span className="category-icon">🗂</span>
          <select
            className="category-select"
            value={activeCategory}
            onChange={(e) => setActiveCategory(e.target.value)}
            aria-label="Filter by category"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Active filter pill */}
      {activeCategory !== 'All' && (
        <div className="active-filter">
          <span>Category: <strong>{activeCategory}</strong></span>
          <button onClick={() => setActiveCategory('All')} aria-label="Clear category">✕</button>
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="no-results">No products found{query ? ` for "${query}"` : ''}{activeCategory !== 'All' ? ` in "${activeCategory}"` : ''}</p>
      ) : (
        <>
          <div className="products-grid">
            {visible.map((key, i) => {
              const file  = `${key}.jpeg`;
              const label = labelFromKey(key);
              const data  = productData[key] || {};
              return (
                <div className="product-card reveal" key={key} style={{ transitionDelay: `${i * 0.12}s` }}>
                  {data.bestseller && <span className="badge">⭐ Bestseller</span>}
                  {data.category && (
                    <span
                      className="category-badge"
                      onClick={() => setActiveCategory(data.category)}
                      title={`Filter by ${data.category}`}
                    >
                      {data.category}
                    </span>
                  )}
                  <div className="product-img-wrap">
                    <img
                      src={`/products/${file}`}
                      alt={label}
                      onError={(e) => {
                        e.target.parentElement.style.background = '#e8f5e9';
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="product-info">
                    <h3>{label}</h3>
                    {data.desc && <p className="product-desc">{data.desc}</p>}
                    {data.benefits && (
                      <p className="product-benefits">✅ {data.benefits}</p>
                    )}
                    {data.keywords && (
                      <div className="product-tags">
                        {data.keywords.slice(0, 4).map((kw) => (
                          <span key={kw} className="tag" onClick={() => setQuery(kw)}>{kw}</span>
                        ))}
                      </div>
                    )}
                    <a
                      href={`https://wa.me/916305214522?text=Hi%2C%20I%20am%20interested%20in%20${encodeURIComponent(label)}%20from%20AdiYogi%20Natural%20Farm.`}
                      className="product-cta"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Enquire Now
                    </a>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Show all / Show less — only when not filtering */}
          {!isFiltering && filtered.length > INITIAL_COUNT && (
            <div className="show-more-wrap">
              {!showAll ? (
                <button className="show-more-btn" onClick={() => setShowAll(true)}>
                  Show All {filtered.length} Products ↓
                </button>
              ) : (
                <button className="show-more-btn outline" onClick={() => { setShowAll(false); document.getElementById('products').scrollIntoView({ behavior: 'smooth' }); }}>
                  Show Less ↑
                </button>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
